import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import Loader from './Loader';
import { OpenSCADViewer } from './OpenSCADViewer';
import { FitToBoundsButton, AutoFitToggle } from './FitToBoundsButton';
import type { ProjectFile } from '@/components/files/FileManager';
import OpenSCADError from '@/lib/OpenSCADError';
import type { InteractiveConfig } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Box, PanelLeftClose } from 'lucide-react';
import { PerformanceDisplay } from './PerformanceDisplay';
import {
  PerformanceStoreContext,
  createViewerPerformanceStore,
} from '@/stores/performanceStore';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  ColorVisibilityToggles,
  toColorKey,
  type CompiledColor,
} from './ColorVisibilityToggles';
import type { ColoredGeometry } from './types';

const MAX_COLOR_SWATCHES = 8;

export interface ViewerSectionProps {
  scadCode?: string;
  isLoading?: boolean;
  projectFiles?: ProjectFile[];
  onError?: (error: OpenSCADError | Error | null) => void;
  onJumpToLine?: (line: number) => void;
  onCollapse?: () => void;
  /** When true, hides the header bar and passes productMode to the 3D scene */
  productMode?: boolean;
  /** When true, hides all chrome entirely; full-quality auto-rotating viewer */
  showcaseMode?: boolean;
  /** Horizontal pixel offset to shift the visual center (for overlay panels) */
  cameraOffsetX?: number;
  /**
   * Called once when the first compile result (geometry) is produced.
   * Used by blog playgrounds to release the init slot so the next
   * queued playground can begin its own initialisation.
   */
  onFirstResult?: () => void;
  /**
   * Called when the fit-to-bounds function becomes available from the scene.
   * Allows a parent component to trigger fit-to-bounds externally.
   */
  onFitToBoundsReady?: (fitFn: () => void) => void;
  /**
   * Externally controlled presentation mode (auto-rotation).
   * When provided, overrides the internal toggle state.
   */
  presentationMode?: boolean;
  /** OpenSCAD parameters — forwarded to JS texture scripts as `params` */
  parameters?: import('@shared/types').Parameter[];
  /**
   * Optional controlled viewer state for command-palette integration.
   */
  viewerState?: import('@/components/viewer/toolbar/ViewerToolbar').ViewerState;
  onViewerStateChange?: (
    updater: (
      prev: import('@/components/viewer/toolbar/ViewerToolbar').ViewerState,
    ) => import('@/components/viewer/toolbar/ViewerToolbar').ViewerState,
  ) => void;
  /** Forwarded to the 3D scene — allows context-menu actions to append code to the editor */
  onInsertCode?: (code: string) => void;
  /** Forwarded to the 3D scene — allows PiP drag-drop to update existing code in the editor */
  onReplaceCode?: (search: string | RegExp, replacement: string) => void;
  /** Forwarded to OpenSCADViewer — called when user clicks an interactive() region */
  onInteraction?: (config: InteractiveConfig) => void;
  /**
   * Increment to force a recompile even when code hasn't changed (e.g. F5 shortcut).
   * Forwarded to OpenSCADViewer which bypasses its compile-hash guards.
   */
  forceRecompileKey?: number;
  /**
   * Called whenever the set of compiled colours changes.
   * Provides the raw RGBA tuples so parent components can map them back to
   * source lines in the code editor's colour indicator bar.
   */
  onCompiledColorsChange?: (colors: [number, number, number, number][]) => void;
  /**
   * Called once the pub/sub subscribe function is ready.
   * Allows external callers (e.g. ParameterSection) to subscribe to pub/sub keys.
   */
  onSubscribeReady?: (
    subscribe: (key: string, callback: (value: unknown) => void) => () => void,
  ) => void;
  /**
   * Called once the pub/sub publish function is ready.
   * Allows external callers (e.g. ParameterSection) to push values into the channel.
   */
  onPubSubPublish?: (publish: (key: string, value: unknown) => void) => void;
  /**
   * Called whenever the effective camera path changes (JS-parsed, fast path).
   * Used by the parent to compute the product readiness score without waiting
   * for a full WASM compile.
   */
  onCameraPathChange?: (path: import('@/worker/types').CameraPathWaypoint[]) => void;
}

export function ViewerSection({
  scadCode,
  isLoading,
  projectFiles,
  onError,
  onJumpToLine,
  onCollapse,
  productMode = false,
  showcaseMode = false,
  cameraOffsetX = 0,
  onFirstResult,
  onFitToBoundsReady,
  presentationMode: externalPresentationMode,
  parameters,
  viewerState: controlledViewerState,
  onViewerStateChange: onControlledViewerStateChange,
  onInsertCode,
  onReplaceCode,
  onInteraction,
  forceRecompileKey,
  onCompiledColorsChange,
  onSubscribeReady,
  onPubSubPublish,
  onCameraPathChange,
}: ViewerSectionProps) {
  const [fitToBounds, setFitToBounds] = useState<(() => void) | null>(null);

  // -----------------------------------------------------------------------
  // Auto-fit state — lifted here so it survives child unmount/remount cycles
  // -----------------------------------------------------------------------

  // Tracks whether the initial fit has ever completed. This ref persists
  // even when the Three.js scene briefly unmounts between recompiles.
  const hasCompletedInitialFit = useRef(false);

  // User-controlled toggle for continuous auto-fit on recompile (default OFF).
  const [autoFitToggle, setAutoFitToggle] = useState(false);

  // Auto-fit is enabled when:
  //   1. The initial fit hasn't happened yet, OR
  //   2. The user explicitly turned on the auto-fit toggle, OR
  //   3. Product/showcase mode is active (no manual fit button available)
  const autoFit =
    !hasCompletedInitialFit.current ||
    autoFitToggle ||
    productMode ||
    showcaseMode;

  // The very first fit should reset camera direction; subsequent fits preserve it.
  const isInitialFit = !hasCompletedInitialFit.current;

  const handleFitToBoundsReady = useCallback(
    (fitFn: () => void) => {
      setFitToBounds(() => fitFn);
      onFitToBoundsReady?.(fitFn);
    },
    [onFitToBoundsReady],
  );

  // Called by SceneBounds after an auto-fit executes
  const handleFitComplete = useCallback(() => {
    if (!hasCompletedInitialFit.current) {
      hasCompletedInitialFit.current = true;
    }
  }, []);

  // Per-viewer performance store — created here (not in ThreeScene) so the
  // context wraps both the header bar (where PerformanceDisplay reads) and
  // the 3D scene (where PerformanceMonitor writes).
  const performanceStore = useMemo(() => createViewerPerformanceStore(), []);

  // -----------------------------------------------------------------------
  // Color visibility toggles
  // -----------------------------------------------------------------------

  /** Deduplicated list of compiled colors (up to MAX_COLOR_SWATCHES). */
  const [compiledColors, setCompiledColors] = useState<CompiledColor[]>([]);

  /** Set of color keys that are currently hidden in the preview. */
  const [hiddenColorKeys, setHiddenColorKeys] = useState<Set<string>>(
    new Set(),
  );

  // Notify parent whenever compiled colours change (for code editor colour bar).
  useEffect(() => {
    onCompiledColorsChange?.(compiledColors.map((c) => c.rgba));
  }, [compiledColors, onCompiledColorsChange]);

  /**
   * Called by OpenSCADViewer whenever a new set of coloredGeometries is ready.
   * Deduplicates by RGBA key, caps at MAX_COLOR_SWATCHES, and clears any
   * stale hidden keys that no longer exist in the new color set.
   */
  const handleColorsDetected = useCallback(
    (geometries: ColoredGeometry[]) => {
      // Deduplicate by color key, preserving isGhost from first occurrence.
      const seen = new Map<string, CompiledColor>();
      for (const cg of geometries) {
        const key = toColorKey(cg.color);
        if (!seen.has(key)) {
          seen.set(key, { key, rgba: cg.color, isGhost: cg.isGhost });
        }
      }

      const deduped = Array.from(seen.values()).slice(0, MAX_COLOR_SWATCHES);
      setCompiledColors(deduped);

      // Remove hidden keys that are no longer in the new color set.
      setHiddenColorKeys((prev) => {
        const validKeys = new Set(deduped.map((c) => c.key));
        const next = new Set<string>();
        for (const k of prev) {
          if (validKeys.has(k)) next.add(k);
        }
        return next.size === prev.size ? prev : next;
      });
    },
    [],
  );

  const handleColorToggle = useCallback((key: string) => {
    setHiddenColorKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return (
    <PerformanceStoreContext.Provider value={performanceStore}>
      <div className="flex h-full w-full flex-col bg-muted/50">
        {/* Header — hidden in product/showcase mode */}
        {!productMode && !showcaseMode && (
          <div className="relative flex h-12 flex-none items-center border-b border-border bg-background px-2">
            {/* Left: collapse button + "Preview" label */}
            <div className="flex items-center gap-0.5">
              {onCollapse && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={onCollapse}
                      >
                        <PanelLeftClose className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Collapse panel</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <span className="text-sm font-medium text-foreground">
                Preview
              </span>
            </div>

            {/* Center: color visibility toggles (absolutely centered) */}
            {compiledColors.length > 0 && (
              <div className="absolute left-1/2 -translate-x-1/2">
                <ColorVisibilityToggles
                  colors={compiledColors}
                  hiddenKeys={hiddenColorKeys}
                  onToggle={handleColorToggle}
                />
              </div>
            )}

            {/* Right: performance display + fit controls */}
            <div className="ml-auto flex items-center gap-0.5">
              <PerformanceDisplay />
              {fitToBounds && (
                <>
                  <AutoFitToggle
                    isEnabled={autoFitToggle}
                    onToggle={setAutoFitToggle}
                  />
                  <FitToBoundsButton onClick={fitToBounds} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="relative flex w-full flex-1 items-center justify-center overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader message="Generating model" />
            </div>
          ) : (
            <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-2">
              {scadCode ? (
                <OpenSCADViewer
                  scadCode={scadCode}
                  projectFiles={projectFiles}
                  onError={onError}
                  onJumpToLine={onJumpToLine}
                  onFitToBoundsReady={handleFitToBoundsReady}
                  autoFit={autoFit}
                  isInitialFit={isInitialFit}
                  onFitComplete={handleFitComplete}
                  productMode={productMode}
                  showcaseMode={showcaseMode}
                  cameraOffsetX={cameraOffsetX}
                  onFirstResult={onFirstResult}
                  presentationMode={externalPresentationMode}
                  parameters={parameters}
                  viewerState={controlledViewerState}
                  onViewerStateChange={onControlledViewerStateChange}
                  onInsertCode={onInsertCode}
                  onReplaceCode={onReplaceCode}
                  onInteraction={onInteraction}
                  forceRecompileKey={forceRecompileKey}
                  onColorsDetected={handleColorsDetected}
                  hiddenColorKeys={hiddenColorKeys}
                  onSubscribeReady={onSubscribeReady}
                  onPubSubPublish={onPubSubPublish}
                  onCameraPathChange={onCameraPathChange}
                />
              ) : (
                <div className="flex flex-col items-center gap-3 text-center">
                  <Box className="h-8 w-8 text-muted-foreground/30" strokeWidth={1.25} />
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-muted-foreground/60">No model yet</p>
                    <p className="text-xs text-muted-foreground/40">
                      Write OpenSCAD code in the editor to see your 3D model here
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PerformanceStoreContext.Provider>
  );
}
